import {
  AddIcon,
  ArchiveIcon,
  ButtonV2,
  CircularLoader,
  DeleteIcon,
  EmptyPage,
  SearchIcon,
  ShareIcon,
  TableHeader,
  Toggle,
  UnarchiveIcon,
} from '@medeloop/medeloop-components-library';
import { EventSourcePolyfill } from 'event-source-polyfill';
import { motion } from 'motion/react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MaterialSymbol } from 'react-material-symbols';
import { useNavigate } from 'react-router-dom';
import { AddFileIcon } from 'src/assets/svg';
import { v4 as uuidv4 } from 'uuid';

import React from 'react';
import { useSelector } from 'react-redux';
import { configuration } from 'src/configuration';
import { DynamoDBEventType } from 'src/enums/dynamodb-event-type';
import { selectIsUserAdmin } from 'src/store/auth';
import { selectAuthToken } from 'src/store/selectors';
import { Cohort, CohortStepStatus, CohortStepType } from 'src/types/cohorts';
import { CohortRow, RowAction } from './cohort-row';

import debounce from 'lodash.debounce';

interface CohortTableProps {
  cohorts: Cohort[];
  isLoading?: boolean;
  onShareCohort?: (cohort: Cohort) => void;
  onDeleteCohort?: (cohort: Cohort) => void;
  onArchiveUnArchiveCohort?: (cohort: Cohort) => void;
  onViewCohort?: (cohort: Cohort) => void;
}

const DEBOUNCE_TIME = 500;

export const CohortTable = ({
  cohorts,
  isLoading = false,
  onShareCohort,
  onDeleteCohort,
  onArchiveUnArchiveCohort,
  onViewCohort,
}: CohortTableProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [cohortsWithUpdates, setCohortsWithUpdates] = useState<Record<string, Cohort>>({});
  const token = useSelector(selectAuthToken);
  const isUserAdmin = useSelector(selectIsUserAdmin);
  const [showArchivedCohorts, setShowArchivedCohorts] = useState<boolean>(false);

  const handleOnSearch = (query: string) => {
    setSearchQuery(query);
  };
  const debounceSearch = debounce(handleOnSearch, DEBOUNCE_TIME);
  // Setup DynamoDB stream for in-progress cohorts
  useEffect(() => {
    const inProgressCohorts = cohorts.filter(
      (cohort) =>
        cohort.currentStep === CohortStepType.Results &&
        cohort.steps?.[CohortStepType.Results]?.status === CohortStepStatus.In_Progress,
    );

    if (inProgressCohorts.length === 0) return;

    // Group cohorts by dataSourceId
    const dataSourceGroups: Record<string, Cohort[]> = {};
    inProgressCohorts.forEach((cohort) => {
      if (!dataSourceGroups[cohort.dataSourceId]) {
        dataSourceGroups[cohort.dataSourceId] = [];
      }
      dataSourceGroups[cohort.dataSourceId].push(cohort);
    });

    // Setup streams for each dataSourceId
    const eventSources: EventSourcePolyfill[] = [];

    Object.entries(dataSourceGroups).forEach(([dataSourceId, groupCohorts]) => {
      const params = new URLSearchParams();
      params.set('pk', `${dataSourceId}`);
      params.set('eventTypes', DynamoDBEventType.Modify);

      const url = `${configuration.accountApiUrl}/sse/dynamodb-stream?${params.toString()}`;

      const es = new EventSourcePolyfill(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      es.onopen = () => {};

      es.onmessage = (e) => {
        try {
          const { content } = JSON.parse(e.data);
          const update = JSON.parse(content);

          const cohortPrefix = 'COHORT#';
          const cohortId = update?.newData.SK.substring(cohortPrefix.length);

          const matchedCohort = groupCohorts.find((c) => c.cohortId === cohortId);
          if (matchedCohort) {
            setCohortsWithUpdates((prev) => ({
              ...prev,
              [cohortId]: {
                ...matchedCohort,
                ...update,
                // Convert DynamoDB format to our format if needed
                steps: update?.newData.Steps ? update.newData.Steps : matchedCohort.steps,
                currentStep: update?.newData.CurrentStep || matchedCohort.currentStep,
                highlightStatus: 'new',
              },
            }));
          }
        } catch (err) {
          console.error('Error processing stream message:', err);
        }
      };

      es.onerror = (err) => {
        console.error(`Stream error for dataSourceId: ${dataSourceId}`, err);
      };

      eventSources.push(es);
    });

    // Cleanup function
    return () => {
      eventSources.forEach((es) => {
        es.close();
      });
    };
  }, [cohorts]);

  const mergedCohorts = useMemo(() => {
    // Merge the original cohorts with any updates we've received
    return cohorts.map((cohort) => {
      if (cohortsWithUpdates[cohort.cohortId]) {
        return cohortsWithUpdates[cohort.cohortId];
      }
      return cohort;
    });
  }, [cohorts, cohortsWithUpdates]);

  const filteredCohorts = useMemo(() => {
    return mergedCohorts
      .filter((cohort) => {
        if (showArchivedCohorts) {
          return true;
        }
        return !cohort.archivedAt;
      })
      .filter((cohort) => {
        if (!searchQuery.trim()) return true;

        return (
          cohort.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (cohort.description || '').toLowerCase().includes(searchQuery.toLowerCase())
        );
      });
  }, [mergedCohorts, searchQuery, showArchivedCohorts]);

  const handleCreateCohort = () => {
    navigate('/data/create-cohort');
  };

  const getTableHeaders = () => [
    { id: uuidv4(), key: 'name', label: t('cohort') },
    { id: uuidv4(), key: 'dataSourceName', label: 'Dataset' },
    { id: uuidv4(), key: 'createdByEmail', label: 'Created by' },
    { id: uuidv4(), key: 'recordCount', label: 'Records' },
    { id: uuidv4(), key: 'createdAt', label: 'Created' },
    { id: uuidv4(), key: 'actions', label: '', sortable: false },
  ];

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <CircularLoader />
      </div>
    );
  }

  if (!cohorts.length) {
    return (
      <EmptyPage
        className="flex flex-col items-center justify-center h-[calc(100vh-350px)] w-full"
        pageIcon={<AddFileIcon className="w-[37px] h-[37px]" />}
        header={t('no_cohorts_created')}
        description={t('no_cohorts_created_description')}
        action={
          <>
            {isUserAdmin ? (
              <button
                onClick={handleCreateCohort}
                className="flex items-center py-2 px-6 bg-cyan-300 hover:bg-cyan-400 text-gray-900 font-medium rounded-full border border-cyan-500 gap-2"
              >
                <MaterialSymbol icon="add" size={20} />
                {t('create_cohort')}
              </button>
            ) : null}
          </>
        }
      />
    );
  }

  // Animation variants
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const headers = getTableHeaders();

  return (
    <div className="flex flex-col gap-6">
      <div className="flex justify-between items-center">
        <div className="w-1/2">
          <div className="flex flex-wrap justify-between items-center p-2 rounded-3xl border border-cool-gray-800 border-solid bg-dark-subdued-background">
            <div className="flex flex-1 items-center">
              <SearchIcon className="text-[#4B5563] ml-4 mr-2 h-[20px] w-[20px]" />
              <input
                type="search"
                title="TableSearchInput"
                placeholder="Search the cohort by name"
                className="flex-1 self-stretch text-sm leading-none text-white min-w-60 bg-transparent outline-none border-none [&::-webkit-search-cancel-button]:hidden placeholder-[#4B5563] h-6"
                aria-label="Search"
                onChange={(e) => debounceSearch(e.target.value)}
              />
            </div>
          </div>
        </div>
        {isUserAdmin ? (
          <>
            <Toggle
              value={showArchivedCohorts}
              label="Show archived cohorts"
              onChange={() => setShowArchivedCohorts((prev) => !prev)}
            />
            <ButtonV2 onClick={handleCreateCohort}>
              <span className="flex gap-2 items-center">
                <AddIcon />
                {t('create_cohort')}
              </span>
            </ButtonV2>
          </>
        ) : null}
      </div>

      <div className="overflow-x-auto overflow-y-auto rounded-lg">
        <table className="w-full">
          <thead className="sticky top-0">
            <tr className="bg-gray-800">
              {headers.map((header) => (
                <TableHeader key={header.id} label={header.label} sortable={header.sortable} />
              ))}
            </tr>
          </thead>
          <motion.tbody
            className="w-full"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {filteredCohorts.map((cohort) => {
              const rowActions: RowAction<Cohort>[] = [
                {
                  name: cohort.archivedAt ? 'Unarchive' : 'Archive',
                  icon: cohort.archivedAt ? <UnarchiveIcon /> : <ArchiveIcon />,
                  onClick: (row) => onArchiveUnArchiveCohort?.(row),
                },
              ];

              if (cohort.archivedAt && onDeleteCohort) {
                rowActions.push({
                  name: <span className="text-destructive-stroke-default">Delete</span>,
                  icon: <DeleteIcon className="text-destructive-stroke-default" />,
                  onClick: (row) => {
                    onDeleteCohort(row);
                  },
                });
              } else if (!cohort.archivedAt && onShareCohort) {
                rowActions.unshift({
                  name: 'Share',
                  icon: <ShareIcon />,
                  onClick: (row) => {
                    onShareCohort(row);
                  },
                });
              }

              return (
                <React.Fragment key={cohort.cohortId}>
                  <CohortRow
                    cohort={cohort}
                    onClick={() =>
                      onViewCohort
                        ? onViewCohort(cohort)
                        : navigate(`/account/data/${cohort.dataSourceId}/cohort/${cohort.cohortId}`)
                    }
                    rowActions={isUserAdmin ? rowActions : null}
                    isHighlighted={
                      cohort.highlightStatus === 'new' || cohort.highlightStatus === 'updated'
                    }
                  />
                </React.Fragment>
              );
            })}
          </motion.tbody>
        </table>
      </div>
    </div>
  );
};
